/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { mixins, SelectInput, colors, ActionButton, fonts } from '@blend/consumer-components';
import { mediaBreakpoints } from '../../../../common/responsive';
import { useActiveSinkId } from '../../../../state/useActiveSinkId/useActiveSinkId';
import { useAudioOutputDevices } from '../../../../hooks/useDevice';
import { SELECTED_AUDIO_OUTPUT_KEY } from '../../../../definitions/devices';

import { useVideoContext } from '../../../../contexts/videoProvider/videoContext';
import soundFile from './HappyBells.mp3';

type SelectInputValue = any;

export namespace AudioOutputDevice {
  export interface Props extends React.HTMLAttributes<HTMLDivElement> {
    name: string;
    sidebarView?: boolean;
    onChange(value: SelectInputValue | null): void;
  }
}

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
  },
  innerWrapper: {
    ...mixins.padding(32, 16),
  },
  inlineWrapper: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    [mediaBreakpoints.mobile]: {
      flexWrap: 'wrap',
    },
  },
  columnWrapper: {
    display: 'flex',
    flexDirection: 'column-reverse',
    width: '100%',
  },
  inputWrapper: {
    width: '60%',
    [mediaBreakpoints.mobile]: {
      width: '100%',
    },
  },
  columnInputWrapper: {
    width: '100%',
    [mediaBreakpoints.mobile]: {
      order: 2,
    },
  },
  buttonWrapper: {
    width: '40%',
    ...mixins.padding(0, 0, 0, 8),
    [mediaBreakpoints.mobile]: {
      width: '100%',
      ...mixins.padding(8, 0, 0),
    },
  },
  button: {
    height: 50,
    paddingTop: 13,
    paddingBottom: 13,
    fontSize: 14,
    fontWeight: fonts.weight.medium,
  },
  columnButtonWrapper: {
    width: '100%',
    ...mixins.padding(0, 0, 8, 0),
    [mediaBreakpoints.mobile]: {
      order: 1,
    },
  },
  input: {
    color: 'green',
    backgroundColor: 'gray',
  },
});
export const AudioOutputDevice: React.FC<AudioOutputDevice.Props> = ({ name, sidebarView, onChange }) => {
  const selectedAudioDeviceId = window.localStorage.getItem(SELECTED_AUDIO_OUTPUT_KEY);
  const { localTracks } = useVideoContext();

  const [soundPlaying, setSoundPlaying] = useState(false);
  const audioOutputDevices = useAudioOutputDevices(localTracks);
  const { activeSinkId, setActiveSinkId } = useActiveSinkId(localTracks);
  const [audioOutputDeviceId, setAudioOutputDeviceId] = useState(activeSinkId);
  const audioEl = useRef<HTMLMediaElement>(null);
  const buttonDisplay = soundPlaying ? 'Playing...' : 'Play sound';

  useEffect(() => {
    if (!audioOutputDeviceId && !!audioOutputDevices[0]) {
      setSoundPlaying(false);
      window.localStorage.setItem(SELECTED_AUDIO_OUTPUT_KEY, audioOutputDevices[0].deviceId);
      setAudioOutputDeviceId(selectedAudioDeviceId || audioOutputDevices[0].deviceId);
    }
  }, [audioOutputDevices, selectedAudioDeviceId]);

  useEffect(() => {
    onChange(audioOutputDeviceId);
  }, [audioOutputDeviceId]);

  const handleChange = useCallback(e => {
    const newActiveSinkId = e.target.value;
    setAudioOutputDeviceId(newActiveSinkId);
    setActiveSinkId(newActiveSinkId);
    (audioEl as any).current.setSinkId(newActiveSinkId);
  }, []);

  const choices = useMemo(
    () =>
      audioOutputDevices.map(device => {
        return { value: device.deviceId, text: device.label };
      }),
    [audioOutputDevices],
  );

  return (
    <div className={css(styles.inlineWrapper, sidebarView && styles.columnWrapper)}>
      <audio
        id="testAudio"
        src={soundFile}
        ref={audioEl}
        onPlay={() => setSoundPlaying(true)}
        onEnded={() => setSoundPlaying(false)}
      />
      <div className={css(styles.inputWrapper, sidebarView && styles.columnInputWrapper)}>
        <SelectInput
          value={audioOutputDeviceId}
          id={name}
          choices={choices}
          onChange={handleChange}
          color={colors.catalystLight40}
          style={styles.input}
          aria-label={'Select speaker device'}
        />
      </div>
      <div className={css(styles.buttonWrapper, sidebarView && styles.columnButtonWrapper)}>
        <ActionButton
          data-dd-privacy="allow"
          sizing={'full'}
          disabled={soundPlaying}
          option={soundPlaying ? 'primary' : 'secondary'}
          onClick={() => audioEl.current!.play()}
          color={colors.catalyst}
          customStyle={styles.button}
        >
          {buttonDisplay}
        </ActionButton>
      </div>
    </div>
  );
};
